import { AbstractViewModel, useViewModel } from '@footsteps/shared';
import type { PdokAddressDocResult } from '@footsteps/types';
import { addressFormSchema } from '../woz-find-address-modal/woz-find-address-modal.schema';
import { SupabaseApiService } from '../../services/supabase-api';
import type {
  SelectAddressesFormValues,
  SelectAddressesFormErrors,
  AddressData,
  WozSelectAddressesModalState,
} from './woz-select-addresses-modal.types';

export class WozSelectAddressesModalViewModel extends AbstractViewModel<WozSelectAddressesModalState> {
  static initialState: WozSelectAddressesModalState = {
    postalCode: '',
    houseNumber: '',
    houseNumberAddition: '',
    errors: {},
    isLoading: false,
    apiError: null,
    results: [],
    selectedAddresses: [],
    canConfirm: false,
  };

  constructor() {
    super(WozSelectAddressesModalViewModel.initialState);
  }

  setPostalCode = (value: string): void => {
    this.update((state) => {
      state.postalCode = value;
    });
  };

  setHouseNumber = (value: string): void => {
    this.update((state) => {
      state.houseNumber = value;
    });
  };

  setHouseNumberAddition = (value: string): void => {
    this.update((state) => {
      state.houseNumberAddition = value;
    });
  };

  private toAddressData(address: PdokAddressDocResult): AddressData {
    return {
      addressPointId: address.addressPointId ?? address.id ?? '',
      addressCity: address.city ?? '',
      addressStreet: address.street ?? '',
      addressHouseNumber: Number(address.houseNumber ?? 0),
      addressHouseLetter: null,
      addressHouseNumberAddition: address.houseNumberAddition ?? null,
      addressPostalCode: address.postalCode ?? '',
    };
  }

  addAddress = (address: PdokAddressDocResult): void => {
    const data = this.toAddressData(address);
    this.update((state) => {
      const exists = state.selectedAddresses.some((a) => a.addressPointId === data.addressPointId);
      if (!exists) {
        state.selectedAddresses.push(data);
        state.canConfirm = true;
      }
    });
  };

  removeAddress = (addressPointId: string): void => {
    this.update((state) => {
      state.selectedAddresses = state.selectedAddresses.filter((a) => a.addressPointId !== addressPointId);
      state.canConfirm = state.selectedAddresses.length > 0;
    });
  };

  isSelected = (addressPointId: string): boolean => {
    return this.state.getValue().selectedAddresses.some((a) => a.addressPointId === addressPointId);
  };

  handleSearch = async (): Promise<void> => {
    const { postalCode, houseNumber, houseNumberAddition } = this.state.getValue();

    this.update((state) => {
      state.errors = {};
      state.apiError = null;
    });

    const parsed = addressFormSchema.safeParse({ postalCode, houseNumber, houseNumberAddition });
    if (!parsed.success) {
      const fieldErrors: SelectAddressesFormErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof SelectAddressesFormValues;
        fieldErrors[field] = issue.message;
      }
      this.update((state) => {
        state.errors = fieldErrors;
      });
      return;
    }

    this.update((state) => {
      state.isLoading = true;
      state.apiError = null;
    });

    try {
      const { postalCode: pc, houseNumber: hn, houseNumberAddition: hna } = parsed.data;
      const api = SupabaseApiService.getInstance();
      const response = await api.lookupAddress(pc, hn, hna || undefined);

      if (response.status === 'not_found' || response.addresses.length === 0) {
        this.update((state) => {
          state.apiError = 'Geen adres gevonden. Controleer de invoer en probeer opnieuw.';
          state.isLoading = false;
          state.results = [];
        });
        return;
      }

      this.update((state) => {
        state.isLoading = false;
        state.results = response.addresses;
      });
    } catch (err) {
      this.update((state) => {
        state.apiError = err instanceof Error ? err.message : 'Er is een fout opgetreden.';
        state.isLoading = false;
        state.results = [];
      });
    }
  };

  handleConfirm = (onConfirm: (addresses: AddressData[]) => void): void => {
    const { selectedAddresses } = this.state.getValue();
    if (selectedAddresses.length === 0) return;
    onConfirm([...selectedAddresses]);
  };

  reset() {
    this.update((state) => {
      Object.assign(state, WozSelectAddressesModalViewModel.initialState);
    });
  }
}

export const useWozSelectAddressesModalViewModel = () =>
  useViewModel<WozSelectAddressesModalState, WozSelectAddressesModalViewModel>(
    () => new WozSelectAddressesModalViewModel()
  );
